import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Progressbar } from 'framework7-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { create, orderBy } from 'lodash';
import mixpanel from 'mixpanel-browser';

import API from '@api/API';

import {
  EntertainerFilter,
  useCurrentEntertainerQuery,
  useFilteredEntertainersQuery,
} from '@queries/EntertainerQueries';
import {
  EventReview,
  SeriesEventBonusTipVotes,
  useEventQuery,
  useEventReviewsQuery,
} from '@queries/EventQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useSubmitBonusTipVoteMutation, useUpdateEventMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery } from '@hooks/useCurrentUserLocation';
import { useDailyLocalStorageReminderIsVisible } from '@hooks/useDailyLocalStorageReminderIsVisible';
import { useReferredBy } from '@hooks/useReferredBy';
import { useShareLink } from '@hooks/useShareLink';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getEntertainerSeoLink } from '@utilities/entertainerUtilities/getEntertainerSeoLink';
import { formatCentsAmountToDollars } from '@utilities/formatCentsAmountToDollars';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import { AdvancedPieChart } from '@components/charts/AdvancedPieChart';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { DangerBanner } from '@components/layout/DangerBanner/DangerBanner';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { EntertainersList } from '@components/layout/Lists/EntertainersList';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useJoinLineup } from '@screens/CommunityScreen/components/ShowsTabContent';
import { CreateEditEventPopup } from '@screens/CreateEditEventScreen/CreateEditEventForm';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import dollarsSvgUrl from '@screens/series/SeriesScreen/dollars.svg';

import { CastReferralPopup } from './CastReferralPopup';
import { TipEntertainerButton, UnconfirmedEntertainerButton } from './EventScreen';

const useSubmitEventCommentMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      eventId,
      distinctId,
      comment,
      referredBy,
    }: {
      eventId: string;
      distinctId: string;
      comment: string;
      referredBy?: string;
    }) =>
      API.post<
        { eventReview: EventReview },
        {
          distinctId: string;
          comment: string;
          referredBy?: string;
        }
      >(`/v1/events/${eventId}/reviews`, {
        distinctId,
        comment,
        referredBy,
      }),
    onSuccess: () => {},
  });

  return {
    isSubmittingEventComment: isPending,
    submitEventCommentAsync: mutateAsync,
  };
};

const useEntertainerFollowUpAuthDialog = () => {
  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { entertainer } = useCurrentEntertainerQuery();
  const { track } = useAnalytics();

  const ENABLE_ENTERTAINER_FOLLOWUP = false;
  const openEntertainerFollowUpAuthDialog = () => {
    if (!entertainer?._id && ENABLE_ENTERTAINER_FOLLOWUP) {
      openConfirmDialog({
        title: 'Are you a drag performer?',
        text: 'Support means more when it comes from a fellow performer!',
        buttonCancel: "No I'm not",
        buttonOk: 'Yes, I am!',
        onConfirm: () => {
          track('Confirm is entertainer');
          createAuthGateHandler(() => {}, {
            entertainerCallback: (entertainerProperties) => {
              track('Entered entertainer properties', entertainerProperties);
            },
          });
        },
      });
    }
  };

  return { openEntertainerFollowUpAuthDialog };
};

const useForceAuth = () => {
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();

  const openAuthGate: typeof createAuthGateHandler = (authCallback) => {
    track('Force auth', {});
    createAuthGateHandler(authCallback, {
      authMessage:
        'A lot of money is on the line! To prevent bots, we require phone number verification, but no worries, it stays confidential and is never shared.',
    });
  };

  return { openAuthGate };
};

const FormContent: FC<{
  hasFeatureBeenPosted?: boolean;
  lastShowFeaturePostDate?: string;
  eventId: string;
  onSubmit: (submittedComment: EventReview) => void;
  forceAuth: boolean;
}> = ({ hasFeatureBeenPosted, lastShowFeaturePostDate, eventId, onSubmit, forceAuth }) => {
  const { track } = useAnalytics();
  const { referredBy } = useReferredBy();

  const didSubmit = useRef(false);
  const distinctId = mixpanel.get_distinct_id();
  const [comment, setComment] = useState('');
  const { isSubmittingEventComment, submitEventCommentAsync } = useSubmitEventCommentMutation();
  const [hasError, setHasError] = useState(false);
  const { openAuthGate } = useForceAuth();
  const { openEntertainerFollowUpAuthDialog } = useEntertainerFollowUpAuthDialog();

  const handleUpdateComment = (updatedComment: string) => {
    setHasError(false);
    setComment(updatedComment);
  };

  const submitButtonText = useMemo(() => {
    if (forceAuth) {
      const submitButtonVariants = ["Let's go", 'Finish hype', 'Share the hype', 'Add to the hype'];

      return submitButtonVariants[Math.floor(Math.random() * submitButtonVariants.length)];
    }

    return 'Leave some hype';
  }, [forceAuth]);

  const handleSubmitComment = async () => {
    if (!didSubmit.current) {
      if (!comment || comment.trim().length < 10) {
        setHasError(true);
        return;
      }

      track('Event Review Submitted', {
        eventId,
        comment,
        submitButtonText,
        forceAuth,
        referredBy,
      });

      didSubmit.current = true;
      try {
        const authCallback = async () => {
          const submittedCommentResponse = await submitEventCommentAsync({
            eventId,
            distinctId,
            comment,
            referredBy,
          });

          if (forceAuth) {
            openEntertainerFollowUpAuthDialog();
          }

          onSubmit(submittedCommentResponse?.eventReview);
        };

        if (forceAuth) {
          openAuthGate(authCallback);
        } else {
          await authCallback();
        }
      } catch (error) {}
      didSubmit.current = false;
    }
  };

  const examples = [
    `"I don't come to many shows, but this one was worth it!!! 🔥🔥🔥"`,
    `"I LOVE coming to shows here! The energy is always unreal 😍"`,
  ];

  return (
    <div>
      <TitleToolbar text="What do you wanna share?" size="sm" />

      {hasFeatureBeenPosted && (
        <div className="mb-2">
          <DangerBanner
            title="Heads up!"
            text={`The roundup for this show has already been shared. New hype won't be part of the tip pool, but the love still counts. Look for a show starting on or after ${dayjs(lastShowFeaturePostDate).add(1, 'day').format('MMM D')} to have your hype count towards the tip pool!`}
          />
        </div>
      )}

      <Textarea
        placeholder="You have not LIVED until you've see this cast perform!! 🔥🔥🔥"
        onChange={handleUpdateComment}
      />

      {hasError && (
        <div className="d-grid gap-1 text-danger mt-2">
          <div>
            This should sound like you're trying to help others decide to attend this show. Talk
            more about what excites you!
          </div>
          {examples.map((example) => (
            <div className="d-flex gap-1">
              <div>•</div>
              <div>
                <em>{example}</em>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2">
        <Button
          disabled={isSubmittingEventComment || !comment}
          onClick={handleSubmitComment}
          roundness="rounded"
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
};

const useUpdateEventReviewUserLikeMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      eventReviewId,
      distinctId,
      isLiked,
    }: {
      eventReviewId: string;
      distinctId: string;
      isLiked: boolean;
    }) =>
      API.post(`/v1/events-reviews/${eventReviewId}/like`, {
        distinctId,
        isLiked,
      }),
    onSuccess: () => {},
  });

  return {
    isUpdatingEventReviewUserLike: isPending,
    updateEventReviewUserLikeAsync: mutateAsync,
  };
};

const EventCommentCard: FC<{ review: EventReview }> = ({ review }) => {
  const { track } = useAnalytics();
  const [isUserLiked, setIsUserLiked] = useState(review.isUserLiked);
  const [likeCount, setLikeCount] = useState(review.likeCount || 0);
  const { isUpdatingEventReviewUserLike, updateEventReviewUserLikeAsync } =
    useUpdateEventReviewUserLikeMutation();

  const handleToggleUserLike = async () => {
    if (isUpdatingEventReviewUserLike) return;

    track('Event Review Like Toggled', {
      eventReviewId: review._id,
      updatedIsLiked: !isUserLiked,
    });

    try {
      setIsUserLiked((prev) => !prev);
      await updateEventReviewUserLikeAsync({
        eventReviewId: review._id,
        distinctId: mixpanel.get_distinct_id(),
        isLiked: !isUserLiked,
      });

      if (!isUserLiked) {
        setLikeCount((prev) => prev + 1);
      } else {
        setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
      }
    } catch (error) {
      // Revert like state on error
      setIsUserLiked((prev) => prev);
    }
  };

  return (
    <Card key={review.postedAt}>
      <div className="p-3">
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          <strong>{`Shared ${dayjs(review.postedAt).fromNow()}`}</strong>
        </div>
        <div className="d-grid gap-2">
          {mapStringToParagraphs(review.comment, {
            collapseTopMargin: true,
            sharedClassName: 'my-0',
          })}
        </div>
        <div className="mt-2">
          <div
            className="d-flex gap-2 align-items-center"
            style={{ fontSize: 14 }}
            onClick={handleToggleUserLike}
          >
            <div style={{ color: isUserLiked ? '#ff0000' : 'inherit' }}>
              <FontAwesomeIcon name="heart" faStyle={isUserLiked ? undefined : 'far'} />
            </div>
            <span>{likeCount}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SHARE_INSTRUCTION_LOCAL_STORAGE_KEY = 'eventReviewsScreenShareInstructionLastViewedAt';
const useShareInstructionIsVisible = (delayMs: number) => {
  const { isDailyLocalStorageReminderVisible, handleDailyLocalStorageReminderAcknowledged } =
    useDailyLocalStorageReminderIsVisible(SHARE_INSTRUCTION_LOCAL_STORAGE_KEY, delayMs, 'month');

  return {
    isShareInstructionVisible: isDailyLocalStorageReminderVisible,
    handleShareInstructionAcknowledged: handleDailyLocalStorageReminderAcknowledged,
  };
};

const InternalShareLinkButton: FC<{
  isLoading: boolean;
  isShareInstructionVisible: boolean;
  onShareInstructionAcknowledged: () => void;
  eventId: string;
  entertainerEventReferralUrl: string | undefined;
}> = ({
  isLoading,
  isShareInstructionVisible,
  onShareInstructionAcknowledged,
  eventId,
  entertainerEventReferralUrl,
}) => {
  if (isLoading) {
    return <></>;
  }

  return (
    <>
      <ShareLinkButton
        url={entertainerEventReferralUrl ?? `https://my.gaggl.app/events/${eventId}/reviews`}
      />
      <PopoverWrapper
        isVisible={false}
        PopoverContent={({ onClose }) => (
          <div className="p-3">
            <div>
              <strong>Quick tip!</strong>
            </div>
            <div className="mt-1">
              <span>
                Click the "Share" button to copy this link to share in your IG story to get more
                hype for this show!
              </span>
            </div>
            <Button
              size="sm"
              className="mt-2 py-2"
              roundness="rounded"
              onClick={() => {
                onShareInstructionAcknowledged();
                onClose();
              }}
            >
              Got it!
            </Button>
          </div>
        )}
      >
        <div />
      </PopoverWrapper>
    </>
  );
};

const ShareSection: FC<{ eventId: string; entertainerEventReferralUrl: string | undefined }> = ({
  eventId,
  entertainerEventReferralUrl,
}) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const url = entertainerEventReferralUrl ?? `https://my.gaggl.app/events/${eventId}/reviews`;

  const handleShareClick = async () => {
    track('Click share voting link inline');
    await shareLink({
      url,
    });
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2 mb-3"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ maxWidth: 'calc(100% - 75px)' }}>
        <div>
          <FontAwesomeIcon name="link" />
        </div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</div>
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Copy'}
        </Button>
      </div>
    </div>
  );
};

const MILESTONES = [5, 10, 15, 25, 40, 60, 100, 150, 250, 400, 600, 1000, 1500, 2000, 2500];

const USE_PROGRESS_BAR = true;
const GREEN = '#31f207';
const YELLOW = '#fedb41';
const RED = '#e13026';

const VoteMilestoneProgress: FC<{
  eventVoteCount: number;
}> = ({ eventVoteCount }) => {
  const [initialVoteCount] = useState(eventVoteCount);

  const currentGoal = useMemo(() => {
    return MILESTONES.find((milestone) => initialVoteCount < milestone);
  }, [initialVoteCount]);

  const percentageOfGoal = eventVoteCount / currentGoal;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfGoal * 100);

    if (percentageInt > 50) {
      return GREEN;
    }

    if (percentageInt > 35) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfGoal]);

  return (
    <div>
      <Progressbar
        style={
          {
            '--f7-progressbar-progress-color': chartColor,
          } as any
        }
        progress={percentageOfGoal * 100}
      />
      <div className="mt-1">
        {eventVoteCount ? (
          <>
            {eventVoteCount === currentGoal ? (
              <strong>Goal reached! 🔥🔥🔥</strong>
            ) : (
              <>
                <strong>{`${eventVoteCount} supporters`}</strong> of {currentGoal} goal
              </>
            )}
          </>
        ) : (
          <strong style={{ borderLeft: `2px solid ${RED}`, paddingLeft: 4 }}>
            Be the first to support this cast!
          </strong>
        )}
      </div>
    </div>
  );
};

const HypeMilestoneProgress: FC<{
  currentUserReviewCount: number;
}> = ({ currentUserReviewCount }) => {
  const currentGoal = 4;

  const percentageOfGoal = currentUserReviewCount / currentGoal;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfGoal * 100);

    if (percentageInt === 100) {
      return GREEN;
    }

    if (percentageInt >= 50) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfGoal]);

  return (
    <div>
      <Progressbar
        style={
          {
            '--f7-progressbar-progress-color': chartColor,
          } as any
        }
        progress={percentageOfGoal * 100}
      />
      <div className="mt-1 text-center" style={{ fontSize: 12 }}>
        <strong
          style={currentUserReviewCount === 0 ? { borderBottom: `1px solid ${RED}` } : undefined}
        >{`${formatCentsAmountToDollars(percentageOfGoal * 500)} potential share`}</strong>{' '}
        of the $5 supporter tip pool
      </div>
    </div>
  );
};

const PercentageOfVotesChart: FC<{
  eventVoteCount: number;
  seriesBonusTipVoteCount: number;
  seriesEventBonusTipVotes: SeriesEventBonusTipVotes;
}> = ({ eventVoteCount, seriesBonusTipVoteCount, seriesEventBonusTipVotes }) => {
  const percentageOfVotes = seriesBonusTipVoteCount ? eventVoteCount / seriesBonusTipVoteCount : 0;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfVotes * 100);

    if (percentageInt > 50) {
      return GREEN;
    }

    if (percentageInt > 35) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfVotes]);

  return (
    <AdvancedPieChart
      data={[
        {
          key: 'others',
          value:
            seriesEventBonusTipVotes.seriesBonusTipVoteCount -
            seriesEventBonusTipVotes.eventVoteCount,
          color: 'rgba(241,241,241, .2)',
        },
        {
          key: 'user',
          value: seriesEventBonusTipVotes.eventVoteCount ?? 0, // currentUserEntryCount,
          color: chartColor,
        },
      ]}
      label={({ dataEntry }) => (dataEntry.key === 'user' ? `${dataEntry.value}%` : '')}
      labelStyle={() => ({
        fill: chartColor,
        fontSize: '12px',
        fontWeight: 'bold',
        fontFamily: 'Rubik',
        letterSpacing: 1,
      })}
    />
  );
};

const EventVoteCard: FC<{
  eventId: string;
  seriesEventBonusTipVotes: SeriesEventBonusTipVotes;
  moveButtons: boolean;
  forceAuth: boolean;
}> = ({ eventId, seriesEventBonusTipVotes, moveButtons, forceAuth }) => {
  const { track, getIpAddress } = useAnalytics();
  const navigate = useNavigate();
  const { submitBonusTipVoteAsync } = useSubmitBonusTipVoteMutation();
  const didSubmitRef = useRef(false);
  const [didVote, setDidVote] = useState(false);
  const [didAttemptVote, setDidAttemptVote] = useState(false);
  const { openAuthGate } = useForceAuth();
  const { openEntertainerFollowUpAuthDialog } = useEntertainerFollowUpAuthDialog();
  const { referredBy } = useReferredBy();

  const { eventVoteCount, seriesBonusTipVoteCount } = useMemo(() => {
    const eventVoteCount = seriesEventBonusTipVotes.eventVoteCount + (didVote ? 1 : 0);
    const seriesBonusTipVoteCount =
      seriesEventBonusTipVotes.seriesBonusTipVoteCount + (didVote ? 1 : 0);

    return { eventVoteCount, seriesBonusTipVoteCount };
  }, [didVote, seriesEventBonusTipVotes]);

  const buttonText = useMemo(() => {
    if (didVote) {
      return 'Thanks for supporting!';
    }

    if (!seriesEventBonusTipVotes.canUserVote) {
      return 'Already supported';
    }

    if (forceAuth) {
      const options = ['Show some love ❤️', 'Support the cast 💸', 'Support for bonus tip 💸'];

      return options[Math.floor(Math.random() * options.length)];
    }

    return 'Support for bonus tip 💸';
  }, [didVote, seriesEventBonusTipVotes.canUserVote, forceAuth]);

  const handleVote = async () => {
    track('Submit review screen vote', {
      selectedPosterId: eventId,
      ipAddress: getIpAddress(),
      seriesBonusTipId: seriesEventBonusTipVotes.seriesBonusTipId,
      buttonText,
      forceAuth,
      referredBy,
    });

    setDidAttemptVote(true);

    if (!didSubmitRef.current) {
      const authCallback = async () => {
        didSubmitRef.current = true;
        await submitBonusTipVoteAsync({
          eventId,
          distinctId: mixpanel.get_distinct_id(),
          seriesBonusTipId: seriesEventBonusTipVotes.seriesBonusTipId,
          regionId: getIpAddress().split('.'),
          showEventReviewSplash: false,
          referredBy,
        });

        if (forceAuth) {
          openEntertainerFollowUpAuthDialog();
        }

        setDidVote(true);
      };

      if (forceAuth) {
        openAuthGate(authCallback);
      } else {
        await authCallback();
      }
    }
  };

  return (
    <>
      <Card>
        <div className="pt-3 pb-2 px-3">
          {USE_PROGRESS_BAR && (
            <div className="mb-2">
              <VoteMilestoneProgress eventVoteCount={eventVoteCount} />
            </div>
          )}
          <div className="d-flex align-items-center gap-3">
            {!USE_PROGRESS_BAR && (
              <div className="d-flex flex-column align-items-center">
                <div style={{ width: 80 }}>
                  <PercentageOfVotesChart
                    eventVoteCount={eventVoteCount}
                    seriesBonusTipVoteCount={seriesBonusTipVoteCount}
                    seriesEventBonusTipVotes={seriesEventBonusTipVotes}
                  />
                </div>
              </div>
            )}
            <div>
              <div>
                The cast of this show could earn a{' '}
                {formatCentsAmountToDollars(seriesEventBonusTipVotes?.seriesBonusTipAmount)} bonus
                tip! Use the button below to show support and then share the link!
              </div>
              {/* <div
            className="d-flex gap-1 align-items-center mt-2 pb-1"
            style={{ fontSize: '.7rem', whiteSpace: 'nowrap' }}
          >
            <span style={{ color: chartColor }}>
              <FontAwesomeIcon name="circle" />
            </span>
            <span>% of votes</span>
          </div> */}
            </div>
          </div>
        </div>
        <Card.Footer>
          {moveButtons ? (
            <Button
              roundness="rounded"
              color="neutral"
              variant="smooth"
              onClick={() => {
                track('Bot detection', {
                  button: 'Vote card',
                });
                navigate(`/noauth/series/denver-drag/bonus-tip-weekend`);
              }}
            >
              More info
            </Button>
          ) : (
            <Button
              roundness="rounded"
              color="neutral"
              variant="smooth"
              disabled={didVote || !seriesEventBonusTipVotes.canUserVote}
              onClick={handleVote}
            >
              {buttonText}
            </Button>
          )}
        </Card.Footer>
      </Card>

      {didAttemptVote && (
        <div className="mt-2">
          <div
            className="relative"
            style={{
              background: 'rgba(255,255,255,.1)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={() => {
              track('Follow to IG');
              navigate(`https://instagram.com/coloradodragguide`);
            }}
          >
            <div className="d-flex gap-2 align-items-center py-1">
              <div className="flex-shrink-0">
                <div className="ps-2" style={{ width: 60, height: 60 }}>
                  <img
                    src={dollarsSvgUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      margin: '0 auto',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
              <div>
                <div>
                  <strong>Be ready for next time!</strong>
                </div>
                <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
                  <span>
                    Tap to learn more to support casts for the next Bonus Tip. We do them often!
                  </span>
                </div>
              </div>
              <div className="ml-auto px-3">
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const EditEventPopup: FC<{ isVisible: boolean; onClose: () => void; eventId: string }> = ({
  isVisible,
  onClose,
  eventId,
}) => {
  const { event: fetchedEvent } = useEventQuery(eventId);
  const { cities } = useCitiesQuery();
  const { updateEventAsync } = useUpdateEventMutation({ eventId });

  const cityOptions = useMemo(() => {
    if (fetchedEvent?.cityId) {
      if (cities?.length) {
        const city = cities.find((city) => city._id === fetchedEvent?.cityId);
        if (city) {
          return [{ label: city.name, value: city._id }];
        }
      }

      return [{ label: fetchedEvent?.cityId, value: fetchedEvent?.cityId }];
    }

    return [];
  }, [fetchedEvent?.cityId, cities]);

  return (
    <CreateEditEventPopup
      isLoading={!fetchedEvent?._id}
      isVisible={isVisible && !!eventId}
      onClose={() => onClose()}
      event={fetchedEvent}
      cityOptions={cityOptions}
      onSubmit={async (payload) => {
        await updateEventAsync(payload);
        onClose();
      }}
    />
  );
};

export const EntertainersSection: FC<{
  eventId: string;
  entertainerEventReferralUrl: string | undefined;
  onOpenCastReferralPopup: () => void;
}> = ({ eventId, onOpenCastReferralPopup, entertainerEventReferralUrl }) => {
  const { referredBy } = useReferredBy();
  const navigate = useNavigate();
  const { eventIsReady, event, refetchEvent } = useEventQuery(eventId);
  const { handleJoinLineup } = useJoinLineup({
    primaryEntertainerType: 'dragPerformer',
    cityId: event?.cityId,
    referredBy,
  });
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();

  const isCurrentEntertainerConfirmed = useMemo(() => {
    return currentEntertainer?._id
      ? event?.entertainers?.some(
          (entertainer) =>
            entertainer.status === 'accepted' && entertainer._id === currentEntertainer?._id,
        )
      : false;
  }, [event?.entertainers, currentEntertainer?._id]);

  const filteredEntertainers = useMemo(() => {
    if (referredBy) {
      return event?.entertainers ?? [];
    }

    return event?.entertainers?.filter((entertainer) => entertainer.status !== 'pending') ?? [];
  }, [event?.entertainers, referredBy]);

  const titleToolbar = useMemo(
    () => (
      <TitleToolbar
        text="💃🏽 Cast Members"
        size="sm"
        action={
          !isCurrentEntertainerConfirmed ? (
            <Button
              size="sm"
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={() => handleJoinLineup(eventId, () => refetchEvent())}
              style={{ padding: '0.25rem 0.75rem' }}
            >
              Are you missing?
            </Button>
          ) : (
            !!entertainerEventReferralUrl && (
              <Button
                size="sm"
                variant="smooth"
                color="neutral"
                roundness="rounded"
                startIcon={<FontAwesomeIcon name="link" />}
                onClick={() => {
                  onOpenCastReferralPopup();
                }}
              >
                Get referral link
              </Button>
            )
          )
        }
      />
    ),
    [],
  );

  if (!eventIsReady) {
    return (
      <>
        {titleToolbar}
        <Skeleton height={100} />
      </>
    );
  }

  return (
    <>
      {titleToolbar}
      {filteredEntertainers.length ? (
        <EntertainersList
          entertainers={filteredEntertainers}
          ListItemAfterContent={({ entertainer }) =>
            entertainer.status !== 'pending' ? (
              entertainer.contact?.venmo && <TipEntertainerButton entertainer={entertainer} />
            ) : (
              <UnconfirmedEntertainerButton
                entertainer={entertainer}
                eventId={eventId}
                onJoinLineup={() => handleJoinLineup(eventId, () => refetchEvent())}
              />
            )
          }
        />
      ) : (
        <div className="mt-2">
          <EmptyState
            title="No Cast Listed"
            text="No entertainers have been listed for this event. Are you a part of the cast? Let everyone know with the button below."
            button={{
              text: 'Add Me to This Lineup',
              onClick: () => handleJoinLineup(eventId, () => refetchEvent()),
              variant: 'smooth',
              color: 'neutral',
              roundness: 'rounded',
            }}
          />
        </div>
      )}
    </>
  );
};

export const EventReviewsScreen = createScreenComponent<{ eventId: string }, { fbclid?: string }>(
  ({ eventId, f7route }) => {
    const distinctId = mixpanel.get_distinct_id();

    const { track } = useAnalytics();
    const { navigate, back } = useRouter();
    const { event, entertainerEventReferralUrl } = useEventQuery(eventId);
    const { eventReviews = [], seriesEventBonusTipVotes } = useEventReviewsQuery(
      eventId,
      distinctId,
    );
    const { currentUser } = useCurrentUser();
    const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();

    const [isCastReferralPopupVisible, setIsCastReferralPopupVisible] = useState(false);
    const [isEditEventPopupVisible, setIsEditEventPopupVisible] = useState(false);
    const [submittedComments, setSubmittedComments] = useState<EventReview[]>([]);

    const [isCommentFormVisible, setIsCommentFormVisible] = useState(false);
    const handleOpenCommentForm = () => {
      setIsCommentFormVisible(true);
    };

    const handleSubmitted = (eventReview: EventReview) => {
      setSubmittedComments((prev) => [...prev, { ...eventReview, isCurrentUsersComment: true }]);
      setIsCommentFormVisible(false);
    };

    const reviews = useMemo<EventReview[]>(() => {
      return orderBy([...eventReviews, ...submittedComments], ['postedAt'], ['desc']);
    }, [submittedComments, eventReviews]);

    const userHasReviewed = useMemo(() => {
      return reviews.some(({ isCurrentUsersComment }) => isCurrentUsersComment);
    }, [reviews]);

    const isEventBeforeToday = useMemo(() => {
      return dayjs(event?.startDate).isBefore(dayjs(), 'day');
    }, [event?.startDate]);

    const IS_HYPE_PAYOUT_ENABLED = false;

    const { isShareInstructionVisible, handleShareInstructionAcknowledged } =
      useShareInstructionIsVisible(500);

    const pastSeries = useMemo(() => {
      if (event?.startDate) {
        const eventStartDate = dayjs(event.startDate);

        return event?.series?.find(
          (series) =>
            series.lastShowFeaturePostDate &&
            eventStartDate.isSameOrBefore(dayjs(series.lastShowFeaturePostDate)),
        );
      }
      return undefined;
    }, [event?.series, event?.startDate]);

    const forceAuth = useMemo(() => {
      // return (
      //   f7route.query?.fbclid ===
      //   'PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQPNTY3MDY3MzQzMzUyNDI3AAGnO5m65Y-XnWoo2PR4zzaerhO7kyUB21gwWkw-vdC4ynFAuvUPh0XrF1XlLhU_aem_8KdJGsOY4YeIxL8XIKfuUw'
      // );
      return reviews?.length > 50;
    }, [f7route.query?.fbclid, currentUser?._id, reviews?.length]);

    const moveButtons = useMemo(() => {
      return false; // !currentUser?._id && reviews?.length > 50;
    }, [reviews?.length]);

    const reviewToggleButton = useMemo(() => {
      if (moveButtons) {
        const options = ['Share hype', 'Add to the hype', 'Post some hype'];

        return options[Math.floor(Math.random() * options.length)];
      }

      return currentUser?._id ? 'Hype and earn more' : 'Leave some hype';
    }, [moveButtons, currentUser?._id]);

    const onEditEvent = () => {
      setIsEditEventPopupVisible(true);
    };

    const didInitPopup = useRef(false);
    useEffect(() => {
      if (!didInitPopup.current && !!event && !isEventBeforeToday) {
        didInitPopup.current = true;
        setIsCastReferralPopupVisible(!!entertainerEventReferralUrl);
      }
    }, [event, isEventBeforeToday, entertainerEventReferralUrl]);

    return (
      <Screen
        name="Event Reviews Screen"
        disableLocationPermissionGate
        headerProps={{
          back: { to: '/noauth/series/denver-drag?tab=postersReviews' },
          title: 'Event Hype',
          right: (
            <InternalShareLinkButton
              isLoading={!event}
              isShareInstructionVisible={
                isShareInstructionVisible &&
                event &&
                !isEventBeforeToday &&
                !isCastReferralPopupVisible
              }
              onShareInstructionAcknowledged={handleShareInstructionAcknowledged}
              eventId={eventId}
              entertainerEventReferralUrl={entertainerEventReferralUrl}
            />
          ),
        }}
        bottomToolbar={
          <Screen.BottomToolbar
            maxWidth={425}
            height={
              currentEntertainer?._id && event && !isEventBeforeToday && IS_HYPE_PAYOUT_ENABLED
                ? 96
                : undefined
            }
          >
            <div className="w-100">
              {currentEntertainer?._id &&
                event &&
                !isEventBeforeToday &&
                IS_HYPE_PAYOUT_ENABLED && (
                  <div className="mb-2">
                    <HypeMilestoneProgress
                      currentUserReviewCount={
                        reviews.filter(({ isCurrentUsersComment }) => isCurrentUsersComment).length
                      }
                    />
                  </div>
                )}

              <div className="d-flex gap-2">
                {moveButtons ? (
                  <Button
                    roundness="rounded"
                    variant="smooth"
                    color="neutral"
                    onClick={() => {
                      track('Bot detection', {
                        button: 'Bottom toolbar',
                      });
                      back(`/noauth/series/denver-drag?tab=postersReviews`);
                    }}
                  >
                    See all shows
                  </Button>
                ) : (
                  <Button
                    roundness="rounded"
                    variant="smooth"
                    color="neutral"
                    onClick={handleOpenCommentForm}
                  >
                    {isEventBeforeToday
                      ? 'Share your thoughts'
                      : currentUser?._id && IS_HYPE_PAYOUT_ENABLED
                        ? 'Hype and earn more'
                        : 'Leave some hype'}
                  </Button>
                )}
              </div>
            </div>
          </Screen.BottomToolbar>
        }
      >
        <Screen.Content>
          {event && (
            <>
              <ShareSection
                eventId={eventId}
                entertainerEventReferralUrl={entertainerEventReferralUrl}
              />

              {currentEntertainer?._id && userHasReviewed && reviews.length < 4 && (
                <div className="mb-3">
                  <DangerBanner
                    title={`${currentEntertainer.title}! You're almost there!`}
                    text={`This show needs ${reviews.length === 3 ? 'one more hype message' : 'a few more hype messages'} to unlock the tip pool. Add ${reviews.length === 3 ? 'one' : 'a couple'} more (you can leave more than one!) to help get it featured.`}
                  />
                </div>
              )}

              {seriesEventBonusTipVotes && !isEventBeforeToday && (
                <div className="mb-3">
                  <EventVoteCard
                    eventId={eventId}
                    seriesEventBonusTipVotes={seriesEventBonusTipVotes}
                    moveButtons={moveButtons}
                    forceAuth={forceAuth}
                  />
                </div>
              )}

              <img className="w-100" src={event.img} style={{ borderRadius: 8 }} />

              {currentUser?.isSuperAdmin && (
                <div>
                  <Button onClick={onEditEvent}>Edit Show</Button>
                </div>
              )}

              {moveButtons && (
                <Button
                  roundness="rounded"
                  variant="smooth"
                  color="neutral"
                  onClick={() => {
                    track('Bot bypass', { reviewToggleButton });
                    handleOpenCommentForm();
                  }}
                >
                  {reviewToggleButton}
                </Button>
              )}

              <EntertainersSection
                eventId={eventId}
                entertainerEventReferralUrl={entertainerEventReferralUrl}
                onOpenCastReferralPopup={() => setIsCastReferralPopupVisible(true)}
              />

              <div className="mt-4">
                {reviews.length ? (
                  <div>
                    <TitleToolbar text="🔥 Hype Messages" size="sm" />
                    <p className="mt-0">
                      Here's what folks are saying about this show! Help grow the audience by
                      leaving your own positive message for folks considering attending!
                    </p>
                    <div>
                      {reviews.map((review) => (
                        <EventCommentCard key={review._id} review={review} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    title="Nothing shared"
                    text={
                      isEventBeforeToday
                        ? 'No one has shared how this show went. Help support the cast by leaving a positive message about the show!'
                        : 'No one has left any hype for this show. Help grow the audience for this show by leaving a positive message for folks considering attending!'
                    }
                    button={{
                      text: isEventBeforeToday ? 'Share your thoughts' : 'Leave some hype',
                      onClick: handleOpenCommentForm,
                    }}
                  />
                )}
              </div>

              <div style={{ height: 48 }} />
            </>
          )}
        </Screen.Content>
        <Popup isVisible={isCommentFormVisible} onClose={() => setIsCommentFormVisible(false)}>
          <Popup.Content>
            {isCommentFormVisible && (
              <FormContent
                hasFeatureBeenPosted={!!pastSeries}
                lastShowFeaturePostDate={pastSeries?.lastShowFeaturePostDate}
                eventId={eventId}
                onSubmit={handleSubmitted}
                forceAuth={forceAuth}
              />
            )}
          </Popup.Content>
        </Popup>
        {currentUser?.isSuperAdmin && (
          <EditEventPopup
            isVisible={isEditEventPopupVisible}
            onClose={() => {
              setIsEditEventPopupVisible(false);
            }}
            eventId={eventId}
          />
        )}
        <CastReferralPopup
          isVisible={isCastReferralPopupVisible}
          onClose={() => {
            setIsCastReferralPopupVisible(false);
          }}
          entertainers={event?.entertainers
            ?.filter(
              (entertainer) =>
                entertainer.status === 'pending' && entertainer._id !== currentEntertainer?._id,
            )
            ?.map((entertainer) => ({
              _id: entertainer._id,
              title: entertainer.title,
              contact: entertainer.contact,
            }))}
          referralUrl={entertainerEventReferralUrl}
        />
      </Screen>
    );
  },
);
